import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity,
  SpecificatieJaarrekening
} from '@snelstart-ventures/fiscaal/domain-logic';
import {BehaviorSubject, filter, Observable, shareReplay, take} from 'rxjs';
import {FormArray, FormControl, FormGroup,} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiButtonModule} from "@snelstart/ui-button";

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box1-winst-uit-onderneming',
  templateUrl: './box1-winst-uit-onderneming.component.html',
  styleUrls: ['./box1-winst-uit-onderneming.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiButtonModule
  ]
})
export class Box1WinstUitOndernemingComponent implements OnInit {

  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzClient = inject(IbRekenService);
  private rapportRequestId = 0;
  public dataVisible$ = new BehaviorSubject<boolean>(false);
  public heeftOndernemingen= false;
  public heeftSamenwerkingsverband= false;
  public isDefAangifte = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroupPerOnderneming = new FormGroup({
    perOnderneming: new FormArray<OmschrijvingBedragFormGroup>([]),
  });

  public formGroup = new FormGroup({
    winstAlleOndernemingen: new FormControl<number | null>(0),
    vrijgesteldeWinst: new FormControl<number | null>(0),
    winstVoorOndernemersaftrek: new FormControl<number | null>(0),
    zelfstandigenaftrek: new FormControl<number | null>(0),
    startersaftrek: new FormControl<number | null>(0),
    zelfstandigenaftrekVoorAOWer: new FormControl<number | null>(0),
    senoaftrek: new FormControl<number | null>(0),
    meewerkaftrek: new FormControl<number | null>(0),
    startersaftrekAO: new FormControl<number | null>(0),
    stakingsaftrek: new FormControl<number | null>(0),
    restant: new FormControl<number | null>(0),
    mkbWinstvrijstelling: new FormControl<number | null>(0),
    nietgerealiseerdezelstandigenaftrek: new FormControl<number | null>(0),
    totaalBenutteStakingsaftrek: new FormControl<number | null>(0),
    dotatieFOR: new FormControl<number | null>(0),
    vrijvalFOR: new FormControl<number | null>(0),
    eindstandFOR: new FormControl<number | null>(0),
    dotatieHIR: new FormControl<number | null>(0),
    vrijvalHIR: new FormControl<number | null>(0),
    eindstandHIR: new FormControl<number | null>(0),
    dotatieKER: new FormControl<number | null>(0),
    vrijvalKER: new FormControl<number | null>(0),
    eindstandKER: new FormControl<number | null>(0),
    mutatieReserves: new FormControl<number | null>(0),
    totaleBeginvermogen: new FormControl<number | null>(0),
    totaleEindvermogen: new FormControl<number | null>(0),
    totaleStortingen: new FormControl<number | null>(0),
    totaleOnttrekkingen: new FormControl<number | null>(0),
    totaleNietAftrekbareKosten: new FormControl<number | null>(0),
    fiscaleWinst: new FormControl<number | null>(0),
    maximaleDotatieFor: new FormControl<number | null>(0),
    fiscaleWinstMinusOA: new FormControl<number | null>(0),
    ondernemersaftrek: new FormControl<number | null>(0),
    belastbareWinst: new FormControl<number | null>(0),
    mkbWinstvrijstellingBijVerlies: new FormControl<number | null>(0),
  });

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(data => {
      this.isDefAangifte = data.taxType === 'DA';
    });

    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((isPartner) => {
      const aangifte = this.dataService.incomeTaxEntity.value.aangifte;
      if (!aangifte) {
        this.dataVisible$.next(false);
        return;
      }
      this.getRapportIfChanged(aangifte, isPartner);
    });
  }

  public getRapportIfChanged(aangifte: Aangifte, partner : boolean) {
    const requestId = ++this.rapportRequestId;
    this.dataVisible$.next(false);

    if (partner) {
      this.heeftOndernemingen = (aangifte.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen?.length ?? 0) > 0;
      this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      })
    }
    else {
      this.heeftOndernemingen = (aangifte.box1?.winstUitOnderneming?.ondernemingen?.length ?? 0) > 0;
      this.bokkzClient.getIncomeTaxReport(aangifte).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      });
    }
  }

  private resetAndPatchForms(details: AangifteRapport): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.formGroupPerOnderneming.reset({}, {emitEvent: false});

    // Patch WUO as is
    this.formGroup.patchValue(details.specificatieWUO?.opbouw ?? {}, {emitEvent: false});

    // Calculate totals for all specificatieJaarrekening
    const jaarrekeningen = details.specificatieJaarrekening ?? [];
    this.updateForm(jaarrekeningen);

    this.formGroup.disable({emitEvent: false});
    this.formGroupPerOnderneming.disable({emitEvent: false});
    this.dataVisible$.next(true);
  }

  get winstPerOnderneming(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroupPerOnderneming.controls.perOnderneming;
  }

  createForm(item?: SpecificatieJaarrekening): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.opbouw?.naamOnderneming ?? null),
      bedrag: new FormControl<number | null>(item?.opbouw?.winstPerOnderneming ?? null),
      vrijgesteldeWinst: new FormControl<number | null>(item?.opbouw?.totaleVrijgesteldeWinst ?? null),
    })
  }

  private updateForm(jaarrekeningen: SpecificatieJaarrekening[]): void {
    let lijst = jaarrekeningen || [];
    const formArray = this.winstPerOnderneming;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      const formValue = {
        omschrijving: lijst[i]?.opbouw?.naamOnderneming ?? null,
        bedrag: lijst[i]?.opbouw?.winstPerOnderneming ?? null,
      };
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(formValue, {emitEvent: false});
      } else {
        const formGroup = this.createForm(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  berekenTotaalOndermersaftrek(): any {
    const totaal = (this.formGroup.get('zelfstandigenaftrek')?.value ?? 0)
      + (this.formGroup.get('zelfstandigenaftrekVoorAOWer')?.value ?? 0)
      + (this.formGroup.get('startersaftrek')?.value ?? 0)
      + (this.formGroup.get('startersaftrekAO')?.value ?? 0)
      + (this.formGroup.get('senoaftrek')?.value ?? 0)
      + (this.formGroup.get('meewerkaftrek')?.value ?? 0)
      + (this.formGroup.get('stakingsaftrek')?.value ?? 0);
    this.formGroup.patchValue({ondernemersaftrek: totaal}, { emitEvent: false });
    return totaal;
  }

  berekenBelastbareWinst(): any {
    const totaal = (this.formGroup.get('winstVoorOndernemersaftrek')?.value ?? 0)
      - (this.formGroup.get('ondernemersaftrek')?.value ?? 0)
      - (this.formGroup.get('mkbWinstvrijstelling')?.value ?? 0);
    this.formGroup.patchValue({belastbareWinst: totaal}, { emitEvent: false });
    return totaal;
  }
}
